/**
 * Per-departure pricing for Ramadhan hotel combinations.
 *
 * A hotel combination carries base prices (quadruplePrice, triplePrice, ...)
 * in cents and an optional `departurePrices` map keyed by departure date
 * ("YYYY-MM-DD"). Each entry may override any subset of the base prices.
 * Mirrors `src/utils/hotel-combination-pricing.ts` in the backend.
 */

export interface CombinationRoomPrices {
  quadruplePrice: number;
  triplePrice: number;
  doublePrice: number;
  singlePrice: number | null;
  childNoBedPrice: number;
  babyPrice: number;
}

/** Baby price used when a combination has none stored (previous fixed amount). */
export const DEFAULT_BABY_PRICE_CENTS = 35000;

export type DeparturePriceOverride = Partial<{
  quadruplePrice: number | null;
  triplePrice: number | null;
  doublePrice: number | null;
  singlePrice: number | null;
  childNoBedPrice: number | null;
  babyPrice: number | null;
}>;

export type DeparturePricesMap = Record<string, DeparturePriceOverride>;

type CombinationLike = {
  quadruplePrice: number;
  triplePrice: number;
  doublePrice: number;
  singlePrice?: number | null;
  childNoBedPrice?: number | null;
  babyPrice?: number | null;
  departurePrices?: DeparturePricesMap | null;
};

/** Normalize any date-ish input to "YYYY-MM-DD" (or "" when unparseable). */
export function toDepartureKey(date: string | Date | null | undefined): string {
  if (!date) return "";
  if (date instanceof Date) {
    return isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
  }
  const str = String(date).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10);
  const parsed = new Date(str);
  return isNaN(parsed.getTime()) ? "" : parsed.toISOString().slice(0, 10);
}

/**
 * Resolve the effective per-person prices (cents) of a combination for a
 * given departure date, applying any override configured for that date.
 */
export function resolveHotelCombinationPrices(
  combination: CombinationLike,
  departureDate: string | Date | null | undefined,
): CombinationRoomPrices {
  const base: CombinationRoomPrices = {
    quadruplePrice: combination.quadruplePrice ?? 0,
    triplePrice: combination.triplePrice ?? 0,
    doublePrice: combination.doublePrice ?? 0,
    singlePrice: combination.singlePrice ?? null,
    childNoBedPrice: combination.childNoBedPrice ?? 0,
    babyPrice: combination.babyPrice ?? DEFAULT_BABY_PRICE_CENTS,
  };

  const key = toDepartureKey(departureDate);
  const map = combination.departurePrices;
  const override = key && map && typeof map === "object" ? map[key] : null;
  if (!override) return base;

  return {
    quadruplePrice: override.quadruplePrice ?? base.quadruplePrice,
    triplePrice: override.triplePrice ?? base.triplePrice,
    doublePrice: override.doublePrice ?? base.doublePrice,
    singlePrice:
      override.singlePrice !== undefined
        ? override.singlePrice
        : base.singlePrice,
    childNoBedPrice: override.childNoBedPrice ?? base.childNoBedPrice,
    babyPrice: override.babyPrice ?? base.babyPrice,
  };
}

/** True when the combination has at least one price override for `departureDate`. */
export function hasDeparturePriceOverride(
  combination: CombinationLike,
  departureDate: string | Date | null | undefined,
): boolean {
  const key = toDepartureKey(departureDate);
  const entry = key ? combination.departurePrices?.[key] : undefined;
  return (
    !!entry && Object.values(entry).some((v) => v !== null && v !== undefined)
  );
}

/**
 * Lowest active per-person price (cents) of a combination across its base
 * prices and every departure listed in `departureDates`. Used for
 * "À partir de" displays.
 */
export function getMinCombinationPrice(
  combination: CombinationLike & {
    isQuadActive?: boolean;
    isTripleActive?: boolean;
    isDoubleActive?: boolean;
    isSingleActive?: boolean;
  },
  departureDates: Array<string | null | undefined> = [],
): number | null {
  const priceSets = [
    resolveHotelCombinationPrices(combination, null),
    ...departureDates.map((d) => resolveHotelCombinationPrices(combination, d)),
  ];
  const prices = priceSets
    .flatMap((set) => [
      combination.isQuadActive !== false ? set.quadruplePrice : null,
      combination.isTripleActive !== false ? set.triplePrice : null,
      combination.isDoubleActive !== false ? set.doublePrice : null,
      combination.isSingleActive === true ? set.singlePrice : null,
    ])
    .filter((p): p is number => typeof p === "number" && p > 0);
  return prices.length > 0 ? Math.min(...prices) : null;
}

/** Departure date keys ("YYYY-MM-DD") of a Ramadhan package, for min-price lookups. */
export function getRamadanDepartureDates(pkg: {
  ramadanDepartures?: Array<{ date?: string | null }> | null;
  fixedDepartureDate?: string | Date | null;
}): string[] {
  const deps = Array.isArray(pkg?.ramadanDepartures)
    ? pkg.ramadanDepartures
    : [];
  const dates = deps
    .map((d) => toDepartureKey(d?.date))
    .filter((d): d is string => !!d);
  if (dates.length === 0) {
    const fixed = toDepartureKey(pkg?.fixedDepartureDate);
    if (fixed) dates.push(fixed);
  }
  return dates;
}
